import React, { useState, useEffect, useRef } from 'react';
import Navbar from './Navbar';
import Login from '../auth/Login';
import Register from '../auth/Register';
import { useNavigate } from 'react-router-dom';

// Create a singleton for auth state that can be shared across components
interface AuthState {
  isLoading: boolean;
  isAuthenticated: boolean;
  userName: string;
  initialized: boolean;
}

// Global auth state and request tracker
const globalAuthState: AuthState = {
  isLoading: true, // Start with loading to prevent flashing
  isAuthenticated: false,
  userName: '',
  initialized: false
};

// In-memory cache to prevent duplicate requests
let authRequestInProgress = false;

interface PageContainerProps {
  children: React.ReactNode;
}

const PageContainer: React.FC<PageContainerProps> = ({ children }) => {
  const [message, setMessage] = useState<string>('');
  const [showLogin, setShowLogin] = useState<boolean>(false);
  const [showRegister, setShowRegister] = useState<boolean>(false);
  const [authState, setAuthState] = useState<AuthState>({
    isLoading: globalAuthState.isLoading,
    isAuthenticated: globalAuthState.isAuthenticated,
    userName: globalAuthState.userName,
    initialized: globalAuthState.initialized
  });
  const initialCheck = useRef(false);
  const navigate = useNavigate();

  const fetchUserData = async (token: string) => {
    // If already authenticating, don't start another request
    if (authRequestInProgress) return;
    
    // If auth state is already initialized and we're logged in, use the cached state
    if (globalAuthState.initialized && globalAuthState.isAuthenticated) {
      setAuthState({
        isLoading: false,
        isAuthenticated: true,
        userName: globalAuthState.userName,
        initialized: true
      });
      return;
    }
    
    try {
      // Mark as in-progress to prevent duplicate requests
      authRequestInProgress = true;
      
      const response = await fetch('/api/auth/me', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const userData = await response.json();
        
        // Update global state
        globalAuthState.isLoading = false;
        globalAuthState.isAuthenticated = true;
        globalAuthState.userName = userData.name;
        globalAuthState.initialized = true;
        
        // Update component state
        setAuthState({
          isLoading: false,
          isAuthenticated: true,
          userName: userData.name,
          initialized: true
        });
      } else {
        // Token is invalid or expired
        localStorage.removeItem('token');
        
        // Update global state
        globalAuthState.isLoading = false;
        globalAuthState.isAuthenticated = false;
        globalAuthState.userName = '';
        globalAuthState.initialized = true;
        
        // Update component state
        setAuthState({
          isLoading: false,
          isAuthenticated: false,
          userName: '',
          initialized: true
        });
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      
      // Update global state
      globalAuthState.isLoading = false;
      globalAuthState.isAuthenticated = false;
      globalAuthState.userName = '';
      globalAuthState.initialized = true;
      
      // Update component state
      setAuthState({
        isLoading: false,
        isAuthenticated: false,
        userName: '',
        initialized: true
      });
    } finally {
      // Mark request as complete
      authRequestInProgress = false;
    }
  };

  // One-time authentication check on mount
  useEffect(() => {
    // Only run once
    if (initialCheck.current) return;
    initialCheck.current = true;
    
    // Set default message
    setMessage('Welcome to Vocab Note!');
    
    // Check if user is logged in - use cached state if available
    if (globalAuthState.initialized) {
      setAuthState({
        isLoading: false,
        isAuthenticated: globalAuthState.isAuthenticated,
        userName: globalAuthState.userName,
        initialized: true
      });
    } else {
      const token = localStorage.getItem('token');
      if (token) {
        fetchUserData(token);
      } else {
        // No token found
        globalAuthState.isLoading = false;
        globalAuthState.isAuthenticated = false;
        globalAuthState.initialized = true;
        
        setAuthState({
          isLoading: false,
          isAuthenticated: false,
          userName: '',
          initialized: true
        });
      }
    }
  }, []);
  
  // Handle auth events (login/logout)
  useEffect(() => {
    // Handler for login event
    const handleLoginEvent = () => {
      const token = localStorage.getItem('token');
      if (token) {
        // Reset global auth state but keep loading state
        globalAuthState.isLoading = true;
        globalAuthState.initialized = false;
        
        // Update component to loading state
        setAuthState(prev => ({
          ...prev,
          isLoading: true
        }));
        
        // Fetch user data
        fetchUserData(token);
      }
    };
    
    // Handler for logout event
    const handleLogoutEvent = () => {
      globalAuthState.isLoading = false;
      globalAuthState.isAuthenticated = false;
      globalAuthState.userName = '';
      
      setAuthState({
        isLoading: false,
        isAuthenticated: false,
        userName: '',
        initialized: true
      });
    };
    
    // Listen for auth events
    window.addEventListener('login', handleLoginEvent);
    window.addEventListener('logout', handleLogoutEvent);
    
    return () => {
      window.removeEventListener('login', handleLoginEvent);
      window.removeEventListener('logout', handleLogoutEvent);
    };
  }, []);

  const handleLogin = () => {
    setShowLogin(true);
  };

  const handleRegister = () => {
    setShowRegister(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    
    // Update global auth state
    globalAuthState.isLoading = false;
    globalAuthState.isAuthenticated = false;
    globalAuthState.userName = '';
    
    // Update component state
    setAuthState({
      isLoading: false,
      isAuthenticated: false,
      userName: '',
      initialized: true
    });
    
    // Dispatch a custom event to notify other components
    window.dispatchEvent(new Event('logout'));
    
    // Navigate to home page
    navigate('/');
  };

  return (
    <div className="App-header">
      <Navbar
        isLoggedIn={authState.isAuthenticated}
        userName={authState.userName}
        message={message}
        onLogin={handleLogin}
        onRegister={handleRegister}
        onLogout={handleLogout}
        isLoading={authState.isLoading}
      />
      {children}
      {showLogin && <Login onClose={() => setShowLogin(false)} />}
      {showRegister && <Register onClose={() => setShowRegister(false)} />}
    </div>
  );
};

export default PageContainer; 